import { useEffect, useState } from "react";

import { Button } from "../../../Components/Buttons";
import { FormInput, FormSection, FormSelect } from "../../../Components/Form";
import { ToastMessage } from "../../../Components/Utils";
import {
  useFetchPageMetaAllQuery,
  useCreatePageMetaMutation,
  useUpdatePageMetaMutation,
} from "../../../stores/api/pageMeta.api";

const PAGES = [
  { value: "default", label: "Default / Fallback", placeholder: "Used when a page has no custom meta" },
  { value: "home", label: "Home Page", placeholder: "Home page" },
  { value: "about", label: "About Page", placeholder: "About page" },
  { value: "contact", label: "Contact Page", placeholder: "Contact page" },
  { value: "blog", label: "Blog Listing", placeholder: "Blog listing page" },
  { value: "categories", label: "Category Listing", placeholder: "Category listing page" },
  { value: "products", label: "Product List", placeholder: "Products listing page" },
  { value: "brands", label: "Brand List", placeholder: "Brands listing page" },
  { value: "faq", label: "FAQ Page", placeholder: "FAQ page" },
  { value: "business-profile", label: "Business Profile", placeholder: "Business Profile" },
  { value: "privacy", label: "Privacy Policy", placeholder: "Privacy policy page" },
  { value: "terms", label: "Terms & Conditions", placeholder: "Terms & conditions page" },
];

const PAGE_OPTIONS = PAGES.map(p => ({ value: p.value, label: p.label }));

// og_tags stored as {key:content} object — convert to/from row array for editing
const ogTagsToRows = (obj = {}) =>
  Object.entries(obj).map(([key, value]) => ({
    key: key.startsWith("og:") ? key.slice(3) : key,
    value,
  }));

const rowsToOgTags = (rows = []) =>
  Object.fromEntries(
    rows
      .filter(r => r.key.trim())
      .map(r => {
        const k = r.key.trim();
        return [k.startsWith("og:") ? k : `og:${k}`, r.value];
      })
  );

const EMPTY_FIELDS = {
  meta_title: "",
  meta_keywords: "",
  meta_description: "",
  og_tag_rows: [],       // [{key:"", value:""}]
  canonical_links: [],   // [{tag:"link", attributes:[{key:"",value:""}]}]
};

// ── Sub-editors ────────────────────────────────────────────────────────────

const OgTagsEditor = ({ rows, onChange }) => {
  const addRow = () => onChange([...rows, { key: "", value: "" }]);
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const updateRow = (i, field, val) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));

  return (
    <div className="space-y-3">
      {rows.map((row, i) => (
        <div key={i} className="flex gap-2 items-start">
          <div className="flex-1">
            <input
              className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400"
              placeholder="og:title"
              value={row.key}
              onChange={e => updateRow(i, "key", e.target.value)}
            />
          </div>
          <div className="flex-[2]">
            <input
              className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400"
              placeholder="content value"
              value={row.value}
              onChange={e => updateRow(i, "value", e.target.value)}
            />
          </div>
          <button
            type="button"
            onClick={() => removeRow(i)}
            className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-red-200 dark:border-red-800 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 dark:border-slate-600 px-3 py-2 text-sm text-gray-500 dark:text-slate-400 hover:border-orange-400 hover:text-orange-500 transition"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        Add OG Tag
      </button>
    </div>
  );
};

const AttributeRow = ({ attr, onUpdate, onRemove }) => (
  <div className="flex gap-2 items-center">
    <input
      className="w-28 shrink-0 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1.5 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-400"
      placeholder="key"
      value={attr.key}
      onChange={e => onUpdate("key", e.target.value)}
    />
    <span className="text-gray-400 text-xs">=</span>
    <input
      className="flex-1 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1.5 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-400"
      placeholder="value"
      value={attr.value}
      onChange={e => onUpdate("value", e.target.value)}
    />
    <button
      type="button"
      onClick={onRemove}
      className="text-red-400 hover:text-red-600 transition"
    >
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
);

const CanonicalLinksEditor = ({ links, onChange }) => {
  const addLink = () =>
    onChange([...links, { tag: "link", attributes: [{ key: "rel", value: "canonical" }, { key: "href", value: "" }] }]);

  const removeLink = (li) => onChange(links.filter((_, i) => i !== li));

  const updateLinkTag = (li, val) =>
    onChange(links.map((l, i) => (i === li ? { ...l, tag: val } : l)));

  const addAttr = (li) =>
    onChange(links.map((l, i) => i === li ? { ...l, attributes: [...l.attributes, { key: "", value: "" }] } : l));

  const removeAttr = (li, ai) =>
    onChange(links.map((l, i) => i === li ? { ...l, attributes: l.attributes.filter((_, j) => j !== ai) } : l));

  const updateAttr = (li, ai, field, val) =>
    onChange(links.map((l, i) =>
      i === li
        ? { ...l, attributes: l.attributes.map((a, j) => j === ai ? { ...a, [field]: val } : a) }
        : l
    ));

  return (
    <div className="space-y-4">
      {links.map((link, li) => (
        <div key={li} className="rounded-xl border border-gray-200 dark:border-slate-700 p-3 space-y-2.5 bg-gray-50 dark:bg-slate-800/50">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500 dark:text-slate-400">tag</span>
              <input
                className="w-24 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-xs text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-orange-400"
                value={link.tag}
                onChange={e => updateLinkTag(li, e.target.value)}
              />
            </div>
            <button
              type="button"
              onClick={() => removeLink(li)}
              className="text-xs text-red-500 hover:text-red-700 transition"
            >
              Remove link
            </button>
          </div>

          <div className="space-y-1.5 pl-2 border-l-2 border-gray-200 dark:border-slate-700">
            {link.attributes.map((attr, ai) => (
              <AttributeRow
                key={ai}
                attr={attr}
                onUpdate={(field, val) => updateAttr(li, ai, field, val)}
                onRemove={() => removeAttr(li, ai)}
              />
            ))}
            <button
              type="button"
              onClick={() => addAttr(li)}
              className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-orange-500 transition mt-1"
            >
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Add attribute
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addLink}
        className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 dark:border-slate-600 px-3 py-2 text-sm text-gray-500 dark:text-slate-400 hover:border-orange-400 hover:text-orange-500 transition"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        Add Link Tag
      </button>
    </div>
  );
};

// ── Main component ──────────────────────────────────────────────────────────

const PageMetaSettings = () => {
  const [selectedPage, setSelectedPage] = useState("default");
  const [allData, setAllData] = useState(
    Object.fromEntries(PAGES.map(p => [p.value, { ...EMPTY_FIELDS }]))
  );
  const { data: existingMetas, isLoading } = useFetchPageMetaAllQuery();
  const [createMeta, { isLoading: isCreating }] = useCreatePageMetaMutation();
  const [updateMeta, { isLoading: isUpdating }] = useUpdatePageMetaMutation();

  const isSaving = isCreating || isUpdating;

  useEffect(() => {
    if (existingMetas) {
      const metas = Array.isArray(existingMetas) ? existingMetas : (existingMetas.data || existingMetas.rows || []);
      const mapped = {};
      metas.forEach((meta) => {
        mapped[meta.page_name] = {
          id: meta.id,
          meta_title: meta.meta_title || "",
          meta_keywords: meta.meta_keywords || "",
          meta_description: meta.meta_description || "",
          og_tag_rows: ogTagsToRows(meta.og_tags || {}),
          canonical_links: meta.canonical_links || [],
        };
      });
      setAllData(prev => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          if (mapped[key]) next[key] = mapped[key];
        });
        return next;
      });
    }
  }, [existingMetas]);

  const page = PAGES.find(p => p.value === selectedPage);
  const formData = allData[selectedPage];

  const setField = (name, value) => {
    setAllData(prev => ({
      ...prev,
      [selectedPage]: { ...prev[selectedPage], [name]: value },
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      page_name: selectedPage,
      meta_title: formData.meta_title,
      meta_keywords: formData.meta_keywords,
      meta_description: formData.meta_description,
      og_tags: rowsToOgTags(formData.og_tag_rows),
      canonical_links: formData.canonical_links,
    };
    try {
      if (formData.id) {
        await updateMeta({ id: formData.id, body: payload }).unwrap();
      } else {
        const result = await createMeta(payload).unwrap();
        if (result?.id) {
          setAllData(prev => ({
            ...prev,
            [selectedPage]: { ...prev[selectedPage], id: result.id },
          }));
        }
      }
      ToastMessage({ type: "success", title: "Saved!", message: `Meta for "${page.label}" saved.` });
    } catch (err) {
      ToastMessage({ type: "error", title: "Error!", message: err?.data?.message || err?.message || "Something went wrong" });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <span className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="mx-auto dark:bg-slate-900 rounded-lg p-6 space-y-5">
      <div>
        <FormSelect
          label="Select Page"
          options={PAGE_OPTIONS}
          value={selectedPage}
          isClearable={false}
          onChange={(val) => val && setSelectedPage(val)}
        />
      </div>

      <form onSubmit={onSubmit} className="space-y-5">
        <FormSection title={page.label}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Meta Title"
              name="meta_title"
              value={formData.meta_title}
              onChange={(val) => setField("meta_title", val)}
              placeholder={`${page.placeholder} title`}
            />
            <FormInput
              label="Meta Keywords"
              name="meta_keywords"
              value={formData.meta_keywords}
              onChange={(val) => setField("meta_keywords", val)}
              placeholder="keyword1, keyword2"
            />
            <FormInput
              label="Meta Description"
              name="meta_description"
              value={formData.meta_description}
              onChange={(val) => setField("meta_description", val)}
              placeholder={`${page.placeholder} description`}
              rows={3}
              className="md:col-span-2"
            />
          </div>
        </FormSection>

        <FormSection title="OG Tags">
          <div className="mb-2 text-xs text-gray-500 dark:text-slate-400">
            Rendered as <code className="bg-gray-100 dark:bg-slate-800 px-1 rounded">&lt;meta property="key" content="value" /&gt;</code>
          </div>
          <OgTagsEditor
            rows={formData.og_tag_rows}
            onChange={(rows) => setField("og_tag_rows", rows)}
          />
        </FormSection>

        <FormSection title="Canonical &amp; Link Tags">
          <div className="mb-2 text-xs text-gray-500 dark:text-slate-400">
            Each entry renders a <code className="bg-gray-100 dark:bg-slate-800 px-1 rounded">&lt;link /&gt;</code> tag with the given attributes.
          </div>
          <CanonicalLinksEditor
            links={formData.canonical_links}
            onChange={(links) => setField("canonical_links", links)}
          />
        </FormSection>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="submit" loading={isSaving} disabled={isSaving}
            className="!bg-orange-500 !text-white hover:!bg-orange-600"
            loadingText="Saving..."
          >
            Save Settings
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PageMetaSettings;
